"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Plus } from "lucide-react";
import { MagneticButton } from "@/components/custom/magnetic-button";

const teamMembers = [
	{
		name: "Shashi Shekhar",
		role: "CEO",
		image: "/about/shekhar.png",
		code: "SSP-01",
		location: "India"
	},
	{
		name: "Suraj Mandal",
		role: "CTO",
		image: "/about/suraj.png",
		code: "SM-02",
		location: "Canada"
	},
	{
		name: "Paragshree Behera",
		role: "Aviation",
		image: "/about/paragshree.png",
		code: "PB-03",
		location: "USA"
	},
	{
		name: "Mani Pratap",
		role: "COO",
		image: "/about/mani.png",
		code: "MP-04",
		location: "India"
	},
];

export default function AboutPageStyle2() {
	const containerRef = useRef(null);
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start start", "end end"],
	});

	const x = useTransform(scrollYProgress, [0, 1], ["0%", "-50%"]);
	const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

	return (
		<div ref={containerRef} className="min-h-screen bg-zinc-950 text-white font-sans selection:bg-white selection:text-black overflow-hidden">

			{/* HERO: The Blueprint */}
			<section className="relative h-screen flex flex-col justify-between p-4 md:p-10 pt-32 border-b border-white/10">
				<div className="flex justify-between items-start">
					<div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
						[ About Us ]
					</div>
					<div className="font-mono text-xs text-zinc-500 uppercase tracking-widest text-right">
						Lat: 28.6139° N <br /> Long: 77.2090° E
					</div>
				</div>

				<div className="relative z-10">
					<h1 className="font-syne text-[18vw] leading-[0.8] font-bold uppercase tracking-tighter text-transparent" style={{ WebkitTextStroke: "2px white" }}>
						The
					</h1>
					<h1 className="font-syne text-[18vw] leading-[0.8] font-bold uppercase tracking-tighter text-transparent ml-[5vw]" style={{ WebkitTextStroke: "2px white" }}>
						High
					</h1>
					<h1 className="font-syne text-[18vw] leading-[0.8] font-bold uppercase tracking-tighter text-white ml-[10vw]">
						Rays
					</h1>
				</div>

				<div className="flex justify-between items-end">
					<div className="w-1/3 h-[1px] bg-white/20"></div>
					<p className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
						Scroll for Schematics
					</p>
				</div>

				{/* Grid Lines */}
				<div className="absolute inset-0 pointer-events-none">
					<div className="absolute left-1/4 top-0 w-[1px] h-full bg-white/5"></div>
					<div className="absolute left-2/4 top-0 w-[1px] h-full bg-white/5"></div>
					<div className="absolute left-3/4 top-0 w-[1px] h-full bg-white/5"></div>
				</div>
			</section>

			{/* MANIFESTO: The Raw Data */}
			<section className="relative py-40 border-b border-white/10">
				<div className="container mx-auto px-4 md:px-10">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-20">
						<div>
							<h2 className="font-syne text-4xl font-bold uppercase mb-8">
								<span className="text-red-500">//</span> What We Do
							</h2>
							<p className="font-inter text-2xl text-zinc-400 leading-relaxed">
								We help creators <span className="text-white">go viral</span> and <span className="text-white">grow audiences</span>. From YouTube promotion to content strategy, we turn channels into success stories.
							</p>
						</div>
						<div className="space-y-12">
							{[
								{ label: "01", title: "YouTube Growth", desc: "Real views, real subscribers. We boost your channel with authentic engagement.", href: "/brands/thehighraysyt" },
								{ label: "02", title: "Content Strategy", desc: "Viral-ready content plans that hit the algorithm and keep audiences hooked.", href: "/brands/thehighraysyt" },
								{ label: "03", title: "Audience Building", desc: "From zero to 100K+. We help creators build loyal, engaged communities.", href: "/brands/thehighraysyt" }
							].map((item, i) => (
								<Link key={i} href={item.href} className="group block border-t border-white/10 pt-8 hover:border-red-500 transition-colors duration-500">
									<div className="flex justify-between items-baseline mb-4">
										<span className="font-mono text-sm text-zinc-500 group-hover:text-red-500 transition-colors">/{item.label}</span>
										<h3 className="font-syne text-3xl font-bold uppercase">{item.title}</h3>
									</div>
									<p className="font-inter text-zinc-500 group-hover:text-zinc-300 transition-colors">{item.desc}</p>
								</Link>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* TEAM: The Film Strip */}
			<section className="relative py-40 overflow-hidden border-b border-white/10">
				<div className="container mx-auto px-4 md:px-10 mb-20">
					<h2 className="font-syne text-[10vw] font-bold uppercase leading-none text-transparent" style={{ WebkitTextStroke: "1px #3f3f46" }}>
						Our Team
					</h2>
				</div>

				<div
					className="flex overflow-x-auto pb-20 gap-8 no-scrollbar snap-x snap-mandatory w-full"
					style={{
						paddingLeft: "max(1rem, calc((100vw - 1200px) / 3.5 + 2.5rem))",
						paddingRight: "max(1rem, calc((100vw - 1200px) / 2 + 2.5rem))"
					}}
				>
					{teamMembers.map((member, i) => (
						<div key={i} className="w-[240px] md:w-[300px] flex-none snap-center group">
							<div className="relative aspect-3/4 bg-zinc-900 border border-white/10 overflow-hidden">
								<Image
									src={member.image}
									alt={member.name}
									fill
									className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-110"
								/>
								<div className="absolute top-4 left-4 font-mono text-xs text-white bg-black/50 backdrop-blur-md px-2 py-1 flex items-center gap-2">
									<span>{member.code}</span>
									<span className="text-zinc-400">|</span>
									<span className="text-red-500">{member.location}</span>
								</div>

								{/* Crosshairs */}
								<div className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
									<div className="absolute top-4 right-4 w-4 h-4 border-t border-r border-red-500"></div>
									<div className="absolute bottom-4 left-4 w-4 h-4 border-b border-l border-red-500"></div>
									<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-px bg-red-500"></div>
									<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-px bg-red-500"></div>
								</div>
							</div>

							<div className="flex justify-between items-center border-b border-l border-r border-white/10 pb-4 group-hover:border-red-500 transition-colors pt-6 px-4">
								<div className="overflow-hidden">
									<h3 className="font-syne text-xl font-bold uppercase text-white truncate">{member.name}</h3>
									<p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-1 truncate">{member.role}</p>
								</div>
								<Plus className="text-zinc-500 group-hover:text-red-500 transition-colors group-hover:rotate-90 duration-300 flex-none ml-2" />
							</div>
						</div>
					))}

					{/* Join Card */}
					<div className="w-[240px] md:w-[300px] flex-none snap-center flex flex-col justify-center items-center border border-dashed border-white/20 aspect-[3/4] hover:border-red-500 hover:bg-red-500/5 transition-all cursor-pointer group">
						<div className="w-20 h-20 rounded-full border border-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
							<Plus className="w-8 h-8 text-zinc-500 group-hover:text-red-500 transition-colors" />
						</div>
						<p className="font-syne text-xl font-bold uppercase mt-6 text-zinc-500 group-hover:text-white transition-colors">Join Our Team</p>
					</div>
				</div>
			</section>

			{/* CTA: The Terminal */}
			<section className="py-40 px-4 md:px-10">
				<div className="bg-zinc-900 border border-white/10 p-10 md:p-20 relative overflow-hidden group">
					<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

					<div className="flex flex-col md:flex-row justify-between items-center gap-10 relative z-10">
						<div>
							<h2 className="font-syne text-5xl md:text-7xl font-bold uppercase leading-none mb-4">
								Initialize <br /> Contact
							</h2>
							<p className="font-mono text-sm text-zinc-500 uppercase tracking-widest">
								// Awaiting Input...
							</p>
						</div>

						<Link href="/contact">
							<MagneticButton className="h-24 w-24 md:h-32 md:w-32 bg-red-600 rounded-none flex items-center justify-center hover:bg-white transition-colors duration-300">
								<ArrowRight className="w-8 h-8 md:w-12 md:h-12 text-black -rotate-45 group-hover:rotate-0 transition-transform duration-500" />
							</MagneticButton>
						</Link>
					</div>

					{/* Background Noise */}
					<div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none"></div>
				</div>
			</section>
		</div>
	);
}
